import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { createClient, SupabaseClient, RealtimeChannel } from '@supabase/supabase-js';
import { UserService } from './user/user.service';

const SUPABASE_URL = 'https://rozsqhogkdlnfivpkamn.supabase.co';
const SUPABASE_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJvenNxaG9na2RsbmZpdnBrYW1uIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc5MTEzNzcsImV4cCI6MjA5MzQ4NzM3N30.H7FLnocozR0pZAlDoNQp5BjF7I0d1Yxp0nk0eiKIQnw';

const LS_KEY = 'gc_changelog_last_seen';
const TABLE_ENTRIES = 'changelog_entries';
const TABLE_SEEN = 'changelog_seen';

export interface ChangelogEntry {
  id: number;
  title: string;
  type: string;
  tags?: string[];
  content: string;
  version: string;
  created_at: string;
}

@Injectable({ providedIn: 'root' })
export class ChangelogBellService implements OnDestroy {
  private supabase: SupabaseClient;
  private channel: RealtimeChannel | null = null;
  private lastSeenTimestamp = 0;

  private unreadCountSubject = new BehaviorSubject<number>(0);
  private hasUnreadSubject = new BehaviorSubject<boolean>(false);
  private unreadEntriesSubject = new BehaviorSubject<ChangelogEntry[]>([]);

  /** Observable con la cantidad de entradas no leídas */
  unreadCount$ = this.unreadCountSubject.asObservable();

  /** Observable booleano: true si hay al menos una entrada no leída */
  hasUnread$ = this.hasUnreadSubject.asObservable();

  /** Observable con las entradas no leídas (ordenadas por fecha desc) */
  unreadEntries$ = this.unreadEntriesSubject.asObservable();

  /** Acceso síncrono al valor actual */
  get unreadCount(): number {
    return this.unreadCountSubject.value;
  }

  get hasUnread(): boolean {
    return this.hasUnreadSubject.value;
  }

  constructor(private userService: UserService) {
    this.supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        autoRefreshToken: false,
        detectSessionInUrl: false,
        persistSession: false,
      },
    });
    this.init().catch(error => {
      console.error('[ChangelogBellService] Error al inicializar:', error);
    });
  }

  // ─── INICIALIZACIÓN ──────────────────────────────────────

  private async init() {
    await this.fetchLastSeenFromSupabase();
    await this.loadAndCount();
    this.subscribeRealtime();
  }

  private async fetchLastSeenFromSupabase() {
    const user = this.userService.getUser();
    if (!user || !user.id) {
      // Fallback a localStorage si no hay usuario
      this.lastSeenTimestamp = this.getLocalLastSeen();
      return;
    }

    try {
      const { data, error } = await this.supabase
        .from(TABLE_SEEN)
        .select('last_seen')
        .eq('user_id', String(user.id))
        .single();

      if (error) {
        // Si no existe el registro, es un error esperado de Supabase (.single())
        this.lastSeenTimestamp = this.getLocalLastSeen();
      } else if (data && data.last_seen) {
        this.lastSeenTimestamp = new Date(data.last_seen).getTime();
        // Sincronizar localStorage
        localStorage.setItem(LS_KEY, String(this.lastSeenTimestamp));
      } else {
        this.lastSeenTimestamp = this.getLocalLastSeen();
      }
    } catch (err) {
      this.lastSeenTimestamp = this.getLocalLastSeen();
    }
  }

  // ─── CARGA DE DATOS Y CONTEO ─────────────────────────────

  private async loadAndCount() {
    const { data, error } = await this.supabase
      .from(TABLE_ENTRIES)
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[ChangelogBellService] Error al cargar entradas:', error.message);
      return;
    }

    const entries: ChangelogEntry[] = data || [];
    const unread = entries.filter(entry => new Date(entry.created_at).getTime() > this.lastSeenTimestamp);

    this.unreadCountSubject.next(unread.length);
    this.hasUnreadSubject.next(unread.length > 0);
    this.unreadEntriesSubject.next(unread);
  }

  // ─── REALTIME ────────────────────────────────────────────

  private subscribeRealtime() {
    this.channel = this.supabase
      .channel('changelog-bell-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: TABLE_ENTRIES }, () => {
        this.loadAndCount();
      })
      .subscribe();
  }

  // ─── MARCAR COMO LEÍDO ──────────────────────────────────

  async markAllSeen(): Promise<void> {
    const now = Date.now();
    this.lastSeenTimestamp = now;
    localStorage.setItem(LS_KEY, String(now));

    // UI Reactiva inmediata
    this.unreadCountSubject.next(0);
    this.hasUnreadSubject.next(false);
    this.unreadEntriesSubject.next([]);

    const user = this.userService.getUser();
    if (user && user.id) {
      // Upsert en Supabase
      const { error } = await this.supabase.from(TABLE_SEEN).upsert(
        {
          user_id: String(user.id),
          last_seen: new Date(now).toISOString(),
        },
        { onConflict: 'user_id' }
      );

      if (error) {
        console.error('[ChangelogBellService] Error al guardar last_seen:', error.message);
      }
    }
  }

  // ─── HELPERS ─────────────────────────────────────────────

  private getLocalLastSeen(): number {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? Number(raw) : 0;
  }

  // ─── CLEANUP ─────────────────────────────────────────────

  ngOnDestroy(): void {
    if (this.channel) {
      this.supabase.removeChannel(this.channel);
    }
  }
}
